import React, { useEffect, useMemo, useRef, useState } from 'react';
import Label from './Label/Label';
import {
  getPhoneFieldCountries,
  getPhoneFieldDefaultCountry,
} from '../../../lib/phoneFieldCountries';
import useCheckoutState from '../../../hooks/useCheckoutState';
import { useCheckoutLocalization } from '../../../hooks/useCheckoutLocalization';
import { usePhoneInputI18n } from '../../../lib/phoneInputI18n';
import { useLazyComponent } from '../../../hooks/useLazyComponent';
import ErrorMessage from './ErrorMessage';
import TextInput from '../../Form/components/TextInput/TextInput';
import { isMobile } from '../../../lib/is-mobile';
import StyledElement from '@repo/checkout/components/StyledElement';
import { useFocusContext } from '../../../contexts/FocusContext';
import clsx from 'clsx';

const errorMap = [
  'Invalid number',
  'Invalid country code',
  'Too short',
  'Too long',
  'Invalid number',
];

const parsePhoneFieldValue = value => {
  if (typeof value != 'string') return;
  // For international numbers starting with +, keep the + for country detection
  // For other cases, replace + with space
  if (value.startsWith('+')) {
    return value; // Keep international format for country detection
  }
  return value.replaceAll('+', ' ');
};

export default function PhoneField({ field, onChange, onBlur }) {
  const { countryCode } = useCheckoutState();
  const { locale } = useCheckoutLocalization();
  const { i18n, ready: isI18nReady } = usePhoneInputI18n(locale);
  const PhoneInput = useLazyComponent(() => import('./PhoneInput'));
  const focusContext = useFocusContext();
  const [isValid, setIsValid] = useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const { label, limitAllowedCountries } = field;
  // A seller default like "+44" only preselects a country, so it must not show "Too short" on load
  const [isTouched, setIsTouched] = useState(
    Boolean(field.value) &&
      field.value !== (field.defaultValue?.enabled ? field.defaultValue.value : undefined),
  );
  const phoneFieldWrapperRef = useRef();
  const phoneFieldRef = useRef();
  const [itiInstance, setItiInstance] = useState(null);
  const [initialValue, setInitialValue] = useState(
    parsePhoneFieldValue(
      field.value
        ? field.value
        : field.defaultValue?.enabled
          ? field.defaultValue?.value
          : undefined,
    ),
  );
  const [isPhoneInputLoaded, setIsPhoneInputLoaded] = useState(false);

  const countries = getPhoneFieldCountries(limitAllowedCountries);

  useEffect(() => {
    setInitialValue(
      parsePhoneFieldValue(
        field.value
          ? field.value
          : field.defaultValue?.enabled
            ? field.defaultValue?.value
            : undefined,
      ),
    );
  }, [field.defaultValue]);

  function handleChange(value) {
    // Prevent setting the same value to prevent infinite loop
    if (field.value === value) return;

    setIsValid(value.isValid);
    setErrorCode(value.errorCode);
    onChange(value);
  }

  const handleFocus = () => {
    if (focusContext) {
      focusContext.setFocused(true, field._id);
    }
  };

  const handleBlur = () => {
    if (focusContext) {
      focusContext.setFocused(false, null);
    }
    onBlur();
  };

  const initialCountry = useMemo(() => {
    if (field.value?.startsWith('+')) return '';

    return getPhoneFieldDefaultCountry(countries, countryCode);
  }, [countries, countryCode, field.value]);

  const isSingleCountry = countries.length === 1;
  const lessThanFiveCountries = countries.length < 5;

  const initOptions = {
    initialCountry,
    formatOnDisplay: true,
    onlyCountries: countries.map(country => country.alpha2),
    showFlags: false,
    ...(isSingleCountry && {
      separateDialCode: field.showSelectedDialCode,
      allowDropdown: false,
      nationalMode: true,
    }),
    ...(!isSingleCountry && {
      separateDialCode: true,
      ...(lessThanFiveCountries && {
        countrySearch: false,
      }),
    }),
    useFullscreenPopup: isMobile(),
    ...(i18n && { i18n }),
  };

  const selectedCountry = itiInstance?.getSelectedCountryData();

  // Prevent unsetting the country with keyboard if already selected
  useEffect(() => {
    if (!phoneFieldRef.current) return;
    if (!itiInstance) return;

    // Only set country if there's no phone number value
    if (!selectedCountry && !initialValue && initialCountry) {
      itiInstance.setCountry(initialCountry);
    }
  }, [initialCountry, phoneFieldRef, field.value, isPhoneInputLoaded]);

  useEffect(() => {
    if (!phoneFieldRef.current) return;
    const iti = phoneFieldRef.current.getInstance();
    setItiInstance(iti);
  }, [isPhoneInputLoaded, phoneFieldRef.current]);

  // reset isTouched when field value is empty
  useEffect(() => {
    if (field.value === '') {
      setIsTouched(false);
    }
  }, [field.value]);

  useEffect(() => {
    if (!phoneFieldWrapperRef.current) return;
    if (isSingleCountry) return;
    if (!isPhoneInputLoaded) return; // Only run after PhoneInput has loaded

    const input = phoneFieldWrapperRef.current.querySelector('input[type="tel"]');

    if (!input) return;

    function increasePaddingLeft() {
      const currentPaddingLeft = Number(
        input.style.getPropertyValue('padding-left').replace('px', ''),
      );

      const newPaddingLeft = currentPaddingLeft + 6;

      input.style.setProperty('padding-left', `${newPaddingLeft}px`);
    }

    input.addEventListener('countrychange', increasePaddingLeft);

    increasePaddingLeft();

    return () => {
      input.removeEventListener('countrychange', increasePaddingLeft);
    };
  }, [phoneFieldWrapperRef.current, isPhoneInputLoaded]);

  return (
    <div ref={phoneFieldWrapperRef}>
      {label && (
        <StyledElement
          as={Label}
          elementPath="checkout.form.fields.label"
          htmlFor={field._id}
          className={clsx('cp_form-label')}
          required={field.required}
        >
          {label}
        </StyledElement>
      )}

      {PhoneInput && isI18nReady ? (
        <PhoneInput
          key={JSON.stringify([
            initOptions.onlyCountries,
            initOptions.showSelectedDialCode,
            initOptions.separateDialCode,
            initialValue,
          ])}
          phoneFieldRef={phoneFieldRef}
          initOptions={initOptions}
          field={field}
          initialValue={initialValue}
          errorCode={errorCode}
          handleChange={handleChange}
          setIsValid={setIsValid}
          setErrorCode={setErrorCode}
          setIsTouched={setIsTouched}
          onLoad={() => setIsPhoneInputLoaded(true)}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      ) : (
        <TextInput className="cp_form-phone w-full invisible" />
      )}

      {!isSingleCountry &&
        isTouched &&
        !isValid &&
        errorCode !== null &&
        errorMap[errorCode] !== undefined && (
          <ErrorMessage variant="error" className="cp_error">
            {errorMap[errorCode]}
          </ErrorMessage>
        )}
    </div>
  );
}
